import { UseFormReturn } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { EVENT_CATEGORIES } from "@/features/supply-config/managed-events-api";
import { getDerivedManagedEvents } from "@/features/supply-config/calendly-api";
import { getTeams } from "@/features/supply-config/teams-api";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ManagedEventWizardData } from "./wizard-form";

export default function StepDetails({
  form,
}: {
  form: UseFormReturn<ManagedEventWizardData>;
}) {
  const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams });
  const { data: derived = [] } = useQuery({
    queryKey: ["calendly-derived-managed-events"],
    queryFn: getDerivedManagedEvents,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-5">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Name <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                list="derived-managed-event-names"
                placeholder="e.g. 1:1 Therapy session (45 minutes)"
              />
            </FormControl>
            <datalist id="derived-managed-event-names">
              {derived.map((d) => (
                <option key={d.name ?? ""} value={d.name ?? ""} />
              ))}
            </datalist>
            <p className="text-[11px] text-gray-400">
              Match the Calendly event name so individual events auto-link
              (suggestions from the staged data).
            </p>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="team_ids"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Teams <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <MultiSelect
                options={teams.map((t) => ({ label: t.name, value: t.id }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select teams"
              />
            </FormControl>
            <p className="text-[11px] text-gray-400">
              Individual event types are generated for every professional across
              the selected teams.
            </p>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {EVENT_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="participant_scope"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Attendees</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Attendees" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                  <SelectItem value="JOINT">Joint</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="duration_minutes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Duration (minutes)</FormLabel>
            <FormControl>
              <Input
                type="number"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value ? Number(e.target.value) : null)
                }
                placeholder="Duration (minutes)"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="round_robin_pool_active"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between border rounded-md p-3">
            <FormLabel className="mb-0">Round-robin pool active</FormLabel>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
